import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { MARKETING_PATHS } from "./paths";

import {
  cadenceImpact,
  checkNewStage,
  checkStageDraft,
  createStage,
  deleteStage,
  emptyDraft,
  invalidateStageSettings,
  saveStage,
  stageRemovalVerdict,
  templatesFor,
} from "./lifecycleStageRules";
import type { StageDraft } from "./lifecycleStageRules";
import {
  checkTrackDraft,
  createTrack,
  defaultTrack,
  setDefaultTrack,
  trackRemovalVerdict,
} from "./lifecycleTrackRules";
import type { ContentAsset } from "./ContentLibrary";
import type { ContactHealth, LifecycleStage, LifecycleTrackRow } from "./types";

/**
 * Lifecycle stages — the cadence settings.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * `public.lifecycle_stages` carries its own reason in the database:
 *
 *     "Stage definitions per track. A table rather than an enum so that
 *      changing a staleness threshold is data, not a migration."
 *
 * It has been a table since migration 048 and has never had an editor, so the
 * one thing it was built to make easy — moving partners from 30 days to 45 —
 * still meant an UPDATE typed into psql against production. That is the same
 * gap Sequences closed for `sequences`: a schema with no authoring surface is
 * a plan, not a feature.
 *
 * WHAT IS EDITABLE HERE
 * ---------------------
 *   stale_after_days      the cadence. The whole point of the table.
 *   default_next_action   the seed wording for this stage's triage task.
 *   sort                  the order every stage picker shows.
 *
 * WHAT IS DELIBERATELY NOT
 * ------------------------
 *   stage, track   the primary key, and `contact_lifecycle_stage_fkey`
 *                  references them with no ON UPDATE clause. Renaming a stage
 *                  in use is refused by the database; renaming an unused one
 *                  strands every reference written to the old name.
 *   is_terminal    read-only. See TerminalNote below for what it decides and
 *                  why a checkbox is the wrong shape for it.
 *   delete, create see NoDeleteNote below. Verified against the live catalog,
 *                  not inferred from a migration file.
 *
 * WHERE THE LOGIC LIVES
 * ---------------------
 * ./lifecycleStageRules.ts, with tests in the `marketing` vitest project.
 * Every refusal on this screen is a rule the DATABASE does not have, so each
 * one is worth a Node assertion at ~1s rather than a browser click at ~39s.
 * lifecycleStages.test.tsx next door only asks whether any of it is wired.
 */

/**
 * How many contact_health rows to read for the per-stage counts.
 *
 * ONE READ, NOT TWELVE. A filtered count per stage would be twelve round
 * trips to draw a screen that is mostly prose; the whole view is 70 rows
 * today. If it ever exceeds this the screen says the numbers are a floor
 * rather than quietly under-reporting the blast radius — which is the one
 * number this page exists to get right.
 */
const HEALTH_PAGE = 500;

const stageKey = (stage: string | null, track: string | null) =>
  `${track ?? "?"}/${stage ?? "?"}`;

export const LifecycleStages = () => {
  const {
    data: stages,
    isPending,
    error,
  } = useGetList<LifecycleStage>("lifecycle_stages", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "sort", order: "ASC" },
    filter: {},
  });

  const { data: trackRows, error: trackError } = useGetList<LifecycleTrackRow>(
    "lifecycle_tracks",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "sort", order: "ASC" },
      filter: {},
    },
  );

  /*
    The library, for the template a stage can suggest. Read once here rather
    than per row: twelve stages would otherwise be twelve identical queries.
  */
  const { data: assetRows } = useGetList<ContentAsset>("content_assets", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
    filter: { is_active: true },
  });

  const {
    data: healthRows,
    total: healthTotal,
    error: healthError,
  } = useGetList<ContactHealth>("contact_health", {
    pagination: { page: 1, perPage: HEALTH_PAGE },
    sort: { field: "id", order: "ASC" },
    filter: {},
  });

  /**
   * ONE `now` for the life of the screen.
   *
   * The impact preview recomputes on every keystroke, and a clock that moves
   * between renders would make the same typed number report different
   * counts — a number that flickers is one nobody trusts, and this one is
   * being used to decide whether to move 33 people.
   */
  const [now] = useState(() => new Date());

  const byStage = useMemo(() => {
    const map = new Map<string, ContactHealth[]>();
    for (const row of healthRows ?? []) {
      if (!row.lifecycle_stage || !row.track) continue;
      const key = stageKey(row.lifecycle_stage, row.track);
      map.set(key, [...(map.get(key) ?? []), row]);
    }
    return map;
  }, [healthRows]);

  const truncated = (healthTotal ?? 0) > (healthRows?.length ?? 0);
  const tracks = useMemo(() => trackRows ?? [], [trackRows]);
  const assets = assetRows ?? [];

  /*
    ONE TRACK AT A TIME.

    Twelve stages across two tracks read as one list of twelve, and the two
    `new` stages sitting in it disagree about the cadence -- which is the
    single most confusing thing this screen used to show. Picking a track
    makes the (stage, track) key visible as the thing it actually is.

    `null` until the tracks load, then the default. NOT a hardcoded
    "prospect": that would put back the constant 084 spent a migration
    removing.
  */
  const [picked, setPicked] = useState<string | null>(null);
  const track = picked ?? defaultTrack(tracks)?.track ?? null;

  const contactsOn = (t: string) =>
    (healthRows ?? []).filter((r) => r.track === t).length;

  return (
    /*
      ONE WIDTH FOR THE WHOLE PAGE, and it is the SAME max-w-4xl that
      LifecycleScreen.tsx uses -- these two are adjacent tabs in the same
      shell, so a different container makes the content jump sideways when
      you switch between them.

      The `p-4` that used to be here is gone, and its absence is the point:
      the inset is ScreenFrame's now. Written here it was DOUBLED inside
      Layout.tsx's own px-4 -- measured at 1024, this h1 began at 32px while
      every other screen's began at 16 -- and on a phone it was the reason
      this screen looked right while its neighbors ran edge to edge.

      This page previously capped nothing and let each paragraph carry its
      own max-w-3xl. The prose therefore stopped at 768px while the stage
      cards, capped by nothing, ran the full 1280px of <main>, and the page
      read as two columns of different widths. A width set per element is a
      width that only holds for the elements somebody remembered.
    */
    <div className="max-w-4xl">
      <h1 className="text-lg font-semibold">Lifecycle stages</h1>

      <p className="mt-1 mb-4 text-sm text-muted-foreground">
        The cadence behind every follow-up in this app. A stage says how long
        somebody may sit untouched before they are overdue, and these numbers
        are data rather than code precisely so that changing one is an edit here
        instead of a migration and a deploy.
      </p>

      <RetroactiveNote />

      {/*
        AN ERROR MUST NOT LOOK LIKE AN EMPTY TABLE. The stages table always
        has twelve rows, so "there are none" is a sentence this screen should
        never print — and it is exactly what a denied read would print. The
        error branch comes first and says what it does not know.
      */}
      {error ? (
        <p className="mt-4 text-sm text-destructive">
          Could not load the lifecycle stages, so this is not a report that
          there are none — it is a report that we do not know.
        </p>
      ) : isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : !stages?.length ? (
        <p className="mt-4 text-sm text-destructive">
          No stages at all. Every contact then joins to no row, loses both the
          stage cadence and the terminal flag, and falls onto contact_health’s
          hard-coded 7-day floor. This is a database problem, not an empty
          screen: migration 048 seeds twelve rows.
        </p>
      ) : (
        <div className="mt-4 space-y-7">
          <TrackBar
            tracks={tracks}
            stages={stages}
            picked={track}
            onPick={setPicked}
            contactsOn={contactsOn}
            failed={Boolean(trackError)}
          />

          {track == null ? (
            <p className="text-sm text-destructive">
              No tracks at all. Every stage references one, so this is a
              database problem rather than an empty screen — migration 084 seeds
              two.
            </p>
          ) : (
            <>
              <StageSection
                stages={stages.filter(
                  (x) => x.track === track && !x.is_terminal,
                )}
                allStages={stages}
                assets={assets}
                track={track}
                byStage={byStage}
                countsUnknown={Boolean(healthError)}
                countsPartial={truncated}
                now={now}
              />
              <GateSection
                stages={stages.filter(
                  (x) => x.track === track && x.is_terminal,
                )}
                allStages={stages}
                track={track}
                byStage={byStage}
                countsUnknown={Boolean(healthError)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The warning that has to sit above the form rather than beside a field.
 *
 * `contact_health` computes `due_at` on every read — there is no stored due
 * date, no trigger and no nightly recomputation — so an edit here is not
 * "from now on". It restates when everybody on the stage WAS due, including
 * the people already overdue.
 */
const RetroactiveNote = () => (
  <p className="rounded-md border border-amber-500/50 bg-amber-500/5 p-3 text-sm">
    <span className="font-medium">These changes are retroactive.</span> Nothing
    stores a due date: <code className="text-xs">contact_health</code>{" "}
    recalculates one from these numbers every time it is read, so moving a
    cadence moves the due date of everyone already on that stage at once —
    backwards as well as forwards. Each stage below says how many people that is
    before you save.
  </p>
);

/**
 * What terminal means, printed on the terminal rows themselves.
 *
 * All three follow-up views — contacts_stalled, contacts_overdue and
 * contacts_no_next_action — carry `NOT is_terminal`, and step 1 of
 * advance_enrollments() ends every active enrollment on a terminal stage with
 * ended_reason 'stage_terminal' BEFORE step 2 writes any task. So the flag is
 * the only permanent exit from the accountability system; everything else is
 * a dated snooze.
 */
const TerminalNote = () => (
  <p className="mt-1 text-xs text-muted-foreground">
    Terminal, and read-only here. A terminal stage has no cadence by database
    rule (<code>lifecycle_stages_terminal_has_no_cadence</code>), drops the
    contact out of all three follow-up views, and makes the nightly job end
    their enrollment with the reason <code>stage_terminal</code> before it
    generates anything. It is the only permanent way out of being chased — every
    other exit is a dated pause — so flipping it changes who is worked, for
    everyone on the stage at once. That belongs in a migration.
  </p>
);

const StageRow = ({
  stage,
  assets,
  allStages,
  rows,
  countsUnknown,
  countsPartial,
  now,
}: {
  stage: LifecycleStage;
  /** The active library, for the template this stage can suggest. */
  assets: ContentAsset[];
  allStages: LifecycleStage[];
  rows: ContactHealth[] | undefined;
  countsUnknown: boolean;
  countsPartial: boolean;
  now: Date;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<StageDraft>(() => emptyDraft(stage));
  const [saving, setSaving] = useState(false);

  const check = checkStageDraft(stage, draft, allStages);
  const set = (patch: Partial<StageDraft>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const onStage = rows?.length ?? 0;
  /*
    THE SAME LIST THE TASK FORM OFFERS, from configuration.taskTypes, and NOT
    a copy written here. 084 validates default_task_type against that column
    with the guard enrollment_steps uses, so a hardcoded list on this screen
    would offer values the database refuses -- and the refusal would arrive as
    a constraint name on save.
  */
  const { taskTypes: TASK_TYPES } = useConfigurationContext();
  /*
    THE TEMPLATE LIST FOLLOWS THE TYPE, using 075's mapping read backwards --
    email is written as an `email` asset, a call as a `call_script`. That
    mapping has exactly two cases and 066 is the reason it is not longer, so a
    demo or a meeting narrows to nothing and is offered everything instead:
    narrowing is help, refusing is a rule, and there is no rule here.
  */
  const choices = templatesFor(
    assets,
    draft.taskType,
    draft.contentId === "" ? null : Number(draft.contentId),
  );
  const id = `${stage.track}-${stage.stage}`;

  /*
   * The preview only appears once the typed cadence is BOTH different and
   * valid. Showing it for "0" or a half-typed "4" would put a number next to
   * an input that cannot be saved, and this number is the one thing on the
   * screen a reader is meant to act on.
   */
  const proposed = check.patch?.stale_after_days ?? null;
  const impact =
    !stage.is_terminal &&
    proposed != null &&
    proposed !== stage.stale_after_days &&
    rows != null
      ? cadenceImpact(rows, stage, proposed, now)
      : null;

  const save = async () => {
    if (!check.patch || !check.changed) return;
    setSaving(true);
    try {
      await saveStage(dataProvider, stage, check.patch);
      // A cadence edit moves four other resources that react-admin has no way
      // to know about; see STAGE_SETTINGS_INVALIDATES.
      await invalidateStageSettings(queryClient);
      notify(`${stage.track}/${stage.stage} saved`, { type: "info" });
    } catch (e) {
      notify(
        `Could not save ${stage.track}/${stage.stage}: ${
          e instanceof Error ? e.message : "unknown error"
        }`,
        { type: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="rounded-md border p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium">{stage.stage}</span>
        <Badge variant={stage.is_terminal ? "outline" : "default"}>
          {stage.is_terminal ? "terminal" : "active stage"}
        </Badge>

        <span className="ml-auto text-xs text-muted-foreground">
          {countsUnknown ? (
            <span className="text-destructive">
              Could not read who is on this stage — this is not a report of
              nobody
            </span>
          ) : onStage === 0 ? (
            "nobody here"
          ) : (
            `${onStage} ${onStage === 1 ? "contact" : "contacts"} here${
              countsPartial ? " (at least)" : ""
            }`
          )}
        </span>
      </div>

      {stage.is_terminal && <TerminalNote />}

      <div className="mt-3 grid gap-3 sm:grid-cols-[8rem_1fr_6rem]">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-days`}>Cadence (days)</Label>
          <Input
            id={`${id}-days`}
            type="number"
            inputMode="numeric"
            min={1}
            value={draft.staleAfterDays}
            disabled={stage.is_terminal}
            placeholder={stage.is_terminal ? "none" : undefined}
            onChange={(e) => set({ staleAfterDays: e.target.value })}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${id}-action`}>Next action</Label>
          <Input
            id={`${id}-action`}
            value={draft.defaultNextAction}
            placeholder="Make first contact"
            onChange={(e) => set({ defaultNextAction: e.target.value })}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${id}-sort`}>Order</Label>
          <Input
            id={`${id}-sort`}
            type="number"
            inputMode="numeric"
            min={1}
            value={draft.sort}
            onChange={(e) => set({ sort: e.target.value })}
          />
        </div>
      </div>

      {/*
        THE TYPE AND THE TEMPLATE BELONG UNDER THE SENTENCE, not beside the
        cadence. They describe the same suggestion: what to say, what kind of
        work it is, and which words to start from.
      */}
      <div className="mt-2 grid gap-3 sm:grid-cols-[10rem_1fr]">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-type`}>Type</Label>
          <select
            id={`${id}-type`}
            value={draft.taskType}
            onChange={(e) => set({ taskType: e.target.value })}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
          >
            {/*
              ONE "None", NOT TWO. configuration.taskTypes carries its own
              `none` entry, so listing it alongside the empty option showed
              the same word twice meaning two things nobody can tell apart:
              "no type set" (null) and "a task of type none". Null is the
              honest one, so the configured duplicate is dropped and the empty
              option says what it is.
            */}
            <option value="">No type</option>
            {TASK_TYPES.filter((t) => t.value !== "none").map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-content`}>Template</Label>
          <div className="flex items-center gap-2">
            <select
              id={`${id}-content`}
              value={draft.contentId}
              onChange={(e) => set({ contentId: e.target.value })}
              className="h-9 min-w-0 flex-1 rounded-md border bg-transparent px-3 text-sm"
            >
              <option value="">None</option>
              {choices.assets.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name}
                </option>
              ))}
            </select>
            {/*
              A LINK OUT, BESIDE THE PICKER. Choosing a template from a list of
              names means trusting a name; the library is one click away so
              somebody can read the words before committing a stage to them.
            */}
            <Link
              to={MARKETING_PATHS.contactContent}
              className="shrink-0 text-xs underline underline-offset-2 text-muted-foreground"
            >
              Review
            </Link>
          </div>
          {/*
            SAY WHEN THE LIST IS SHORTER THAN THE LIBRARY. A picker that
            quietly hides most of its options reads as a library with three
            things in it.
          */}
          {/*
            THE REASON, NOT AN EMPTY SELECT. A picker with nothing in it and no
            explanation reads as a library that failed to load — and for Ship
            or Lunch the list is empty on purpose, because a template is the
            words of a message and those are not messages.
          */}
          {choices.reason ? (
            <p className="text-xs text-muted-foreground">{choices.reason}</p>
          ) : choices.filtered ? (
            <p className="text-xs text-muted-foreground">
              Showing{" "}
              {choices.kind === "call_script"
                ? "call scripts"
                : "email templates"}{" "}
              only.
            </p>
          ) : null}
        </div>
      </div>

      <NextActionHelp />

      {impact && <ImpactNote impact={impact} days={proposed as number} />}

      {check.errors.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm text-destructive">
          {check.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          disabled={!check.changed || check.errors.length > 0 || saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!check.changed || saving}
          onClick={() => setDraft(emptyDraft(stage))}
        >
          Reset
        </Button>
      </div>
    </li>
  );
};

/**
 * What `default_next_action` is for, next to the box that edits it.
 *
 * WRITTEN, AND READ BY NOTHING. Verified 2026-09-04 against the live
 * database: no view and no function in `public` or `private` references this
 * column, and nothing in the app renders it. Saying so is the point — a field
 * whose effect a reader has to guess at gets filled in with hope, and the
 * honest description is that this is the wording, held next to the cadence it
 * belongs with, ready for the triage task to carry.
 */
const NextActionHelp = () => (
  <p className="mt-1 text-xs text-muted-foreground">
    The sentence somebody should see next to a contact who has gone quiet on
    this stage — “Make first contact”, “Keep the relationship warm”. It is the
    default wording for the triage task, kept here so it stays in step with the
    cadence rather than being retyped per contact. As of 2026-09-04 no database
    view or function reads this column, so editing it changes the wording this
    table holds and nothing else yet.
  </p>
);

/**
 * How many people the typed cadence would move, before it is saved.
 *
 * "43 people by accident" is the failure this exists to stop, and a raw
 * headcount is not enough on its own: what a reader needs is the DELTA, since
 * a cadence change usually moves the due date of everybody and the follow-up
 * status of only a few.
 */
const ImpactNote = ({
  impact,
  days,
}: {
  impact: ReturnType<typeof cadenceImpact>;
  days: number;
}) => (
  <div className="mt-2 rounded-md border border-amber-500/50 bg-amber-500/5 p-2 text-sm">
    <p>
      Saving {days} {days === 1 ? "day" : "days"} moves the due date of{" "}
      <span className="font-medium">
        {impact.following} {impact.following === 1 ? "contact" : "contacts"}
      </span>{" "}
      on this stage.
    </p>
    <p className="mt-1">
      {impact.startChasing === 0 && impact.stopChasing === 0
        ? "Nobody starts or stops being overdue today, but every future due date shifts."
        : [
            impact.startChasing > 0
              ? `${impact.startChasing} would become overdue immediately`
              : null,
            impact.stopChasing > 0
              ? `${impact.stopChasing} would stop being overdue`
              : null,
          ]
            .filter(Boolean)
            .join(", ") + "."}{" "}
      Overdue on this stage today: {impact.overdueNow}.
    </p>
    {(impact.overridden > 0 || impact.paused > 0) && (
      <p className="mt-1 text-xs text-muted-foreground">
        Not counted:{" "}
        {impact.overridden > 0 &&
          `${impact.overridden} on a per-contact cadence of their own, which beats this default`}
        {impact.overridden > 0 && impact.paused > 0 && "; "}
        {impact.paused > 0 &&
          `${impact.paused} paused, and a pause outranks the clock until it expires`}
        .
      </p>
    )}
  </div>
);

export default LifecycleStages;

/* ── the track bar ─────────────────────────────────────────────────────── */

/**
 * Which track is on screen, which one new contacts start on, and adding one.
 *
 * ONE TRACK AT A TIME IS THE POINT. Twelve stages across two tracks read as
 * one list of twelve, and the two `new` stages in it disagree about the
 * cadence -- which is the single most confusing thing this screen used to
 * show. Picking a track makes (stage, track) visible as the compound key it
 * is.
 */
const TrackBar = ({
  tracks,
  stages,
  picked,
  onPick,
  contactsOn,
  failed,
}: {
  tracks: LifecycleTrackRow[];
  stages: LifecycleStage[];
  picked: string | null;
  onPick: (track: string) => void;
  contactsOn: (track: string) => number;
  failed: boolean;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ label: "", cadenceDays: "" });

  const current = tracks.find((t) => t.track === picked) ?? null;
  const check = checkTrackDraft(draft, tracks);
  const removal = current
    ? trackRemovalVerdict(current, stages, contactsOn(current.track), tracks)
    : { ok: false as const, reason: "No track selected." };

  const run = async (what: string, action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      await invalidateStageSettings(queryClient);
      notify(what, { type: "success" });
    } catch (e) {
      notify((e as Error).message ?? "That did not work", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (failed)
    return (
      <p className="text-sm text-destructive">
        Could not read the tracks, so this is not a report that there are none —
        it is a report that we do not know.
      </p>
    );

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="track-picker">Track</Label>
          <select
            id="track-picker"
            value={picked ?? ""}
            onChange={(e) => onPick(e.target.value)}
            className="h-9 rounded-md border bg-transparent px-3 text-sm"
          >
            {tracks.map((t) => (
              <option key={t.track} value={t.track}>
                {t.label}
                {t.is_default ? " — default" : ""}
              </option>
            ))}
          </select>
        </div>

        <Button
          size="sm"
          variant="outline"
          className="h-9"
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? "Cancel" : "+ Track"}
        </Button>

        {current && !current.is_default && (
          <Button
            size="sm"
            variant="ghost"
            className="h-9"
            disabled={busy}
            onClick={() =>
              run(`New contacts now start on ${current.label}.`, () =>
                setDefaultTrack(dataProvider, tracks, current.track),
              )
            }
          >
            Make this the default
          </Button>
        )}

        {current && (
          <Button
            size="sm"
            variant="ghost"
            className="h-9 ml-auto text-destructive"
            disabled={busy || !removal.ok}
            title={removal.ok ? undefined : removal.reason}
            onClick={() =>
              run(`${current.label} removed.`, () =>
                dataProvider.delete("lifecycle_tracks", {
                  id: current.track,
                  previousData: current,
                }),
              )
            }
          >
            Remove track
          </Button>
        )}
      </div>

      {/*
        THE DEFAULT ONLY PRESELECTS. Saying so matters: a reader could
        reasonably assume it assigns a track to everybody who has none, and
        the whole accountability layer depends on "nobody has triaged this
        yet" staying visible rather than being quietly filled in.
      */}
      {current?.is_default && (
        <p className="text-xs text-muted-foreground">
          Preselected when somebody triages a contact who has no stage yet. It
          does not assign a track to anyone on its own.
        </p>
      )}

      {current && !removal.ok && (
        <p className="text-xs text-muted-foreground">{removal.reason}</p>
      )}

      {adding && (
        <div className="space-y-2 border-t pt-3">
          {/*
            A TRACK IS CREATED WITH A GATE, ALWAYS. A terminal stage is the
            only permanent exit from the accountability views, so a track
            without one chases everybody on it for ever at the 7-day floor and
            nothing reports it. The database cannot enforce that -- it is a
            cross-row rule and a trigger would refuse the first insert -- so
            the guarantee is that this is the only way to make one.
          */}
          <p className="text-xs text-muted-foreground">
            A new track starts with one stage and one gate. Without a gate,
            nobody on the track could ever be closed.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="new-track-label">Name</Label>
              <Input
                id="new-track-label"
                value={draft.label}
                placeholder="Customers"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, label: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1 w-32">
              <Label htmlFor="new-track-cadence">Chase every</Label>
              <Input
                id="new-track-cadence"
                type="number"
                inputMode="numeric"
                min={1}
                value={draft.cadenceDays}
                placeholder="30"
                onChange={(e) =>
                  setDraft((d) => ({ ...d, cadenceDays: e.target.value }))
                }
              />
            </div>
            <Button
              size="sm"
              className="h-9"
              disabled={busy || !check.ok}
              onClick={() => {
                if (!check.ok) return;
                void run(`${draft.label} added, with a stage and a gate.`, () =>
                  createTrack(dataProvider, check.plan),
                ).then(() => {
                  setAdding(false);
                  setDraft({ label: "", cadenceDays: "" });
                  onPick(check.plan.track.track);
                });
              }}
            >
              Add track
            </Button>
          </div>
          {check.errors.length > 0 && draft.label !== "" && (
            <ul className="space-y-1 text-xs text-destructive">
              {check.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

/* ── the two sections ──────────────────────────────────────────────────── */

const AddStage = ({
  track,
  allStages,
  terminal,
}: {
  track: string;
  allStages: LifecycleStage[];
  terminal: boolean;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ name: "", cadenceDays: "" });

  const check = checkNewStage(
    { ...draft, isTerminal: terminal },
    track,
    allStages,
  );

  return (
    <div className="pt-1">
      <Button
        size="sm"
        variant="outline"
        className="h-7"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Cancel" : terminal ? "+ Gate" : "+ Stage"}
      </Button>

      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={`new-${track}-${terminal}`}>Name</Label>
            <Input
              id={`new-${track}-${terminal}`}
              value={draft.name}
              placeholder={terminal ? "churned" : "negotiating"}
              onChange={(e) =>
                setDraft((d) => ({ ...d, name: e.target.value }))
              }
            />
          </div>
          {/* NO CADENCE FIELD ON A GATE. 084 refuses one, so offering the box
              would be offering a value the database rejects. */}
          {!terminal && (
            <div className="space-y-1 w-32">
              <Label htmlFor={`new-days-${track}`}>Cadence (days)</Label>
              <Input
                id={`new-days-${track}`}
                type="number"
                inputMode="numeric"
                min={1}
                value={draft.cadenceDays}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, cadenceDays: e.target.value }))
                }
              />
            </div>
          )}
          <Button
            size="sm"
            className="h-9"
            disabled={busy || !check.ok}
            onClick={async () => {
              if (!check.ok) return;
              setBusy(true);
              try {
                await createStage(dataProvider, check.row);
                await invalidateStageSettings(queryClient);
                notify(`${check.row.stage} added.`, { type: "success" });
                setOpen(false);
                setDraft({ name: "", cadenceDays: "" });
              } catch (e) {
                notify((e as Error).message ?? "That did not work", {
                  type: "error",
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            Add
          </Button>
          {check.errors.length > 0 && draft.name !== "" && (
            <ul className="w-full space-y-1 text-xs text-destructive">
              {check.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const StageSection = ({
  stages,
  allStages,
  assets,
  track,
  byStage,
  countsUnknown,
  countsPartial,
  now,
}: {
  stages: LifecycleStage[];
  allStages: LifecycleStage[];
  assets: ContentAsset[];
  track: string;
  byStage: Map<string, ContactHealth[]>;
  countsUnknown: boolean;
  countsPartial: boolean;
  now: Date;
}) => (
  <section className="space-y-2">
    <div>
      <h2 className="text-base font-semibold">Stages</h2>
      <p className="text-sm text-muted-foreground">
        How often somebody on this stage is chased, and what to do when they go
        quiet.
      </p>
    </div>
    {stages.length === 0 ? (
      <p className="text-sm text-destructive">
        This track has no live stages, so there is nowhere to put anybody on it.
        Add one.
      </p>
    ) : (
      <ul className="space-y-3 rounded-md border divide-y">
        {stages.map((stage) => (
          <StageRow
            key={stage.id}
            stage={stage}
            assets={assets}
            allStages={allStages}
            rows={byStage.get(stageKey(stage.stage, stage.track))}
            countsUnknown={countsUnknown}
            countsPartial={countsPartial}
            now={now}
          />
        ))}
      </ul>
    )}
    <AddStage track={track} allStages={allStages} terminal={false} />
  </section>
);

/**
 * The gates, given their own section and their own weight.
 *
 * THEY ARE THE ONLY PERMANENT EXIT from the accountability views, which makes
 * them the most consequential rows on this screen and the ones that used to
 * be hardest to find -- they sat in the same list as the live stages, wearing
 * a badge, offering a cadence box the database refuses and a next-action box
 * it also refuses.
 *
 * NO CADENCE, NO NEXT ACTION, NO ORDER. The first two because 084 refuses
 * them; the third because a gate's position carries no meaning. An ending is
 * at the end, so the sort is chosen for it rather than typed.
 */
const GateSection = ({
  stages,
  allStages,
  track,
  byStage,
  countsUnknown,
}: {
  stages: LifecycleStage[];
  allStages: LifecycleStage[];
  track: string;
  byStage: Map<string, ContactHealth[]>;
  countsUnknown: boolean;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  return (
    <section className="space-y-2 rounded-md border border-amber-500/50 bg-amber-500/5 p-3">
      <div>
        <h2 className="text-base font-semibold">Gates</h2>
        <p className="text-sm text-muted-foreground">
          The only way off this track for good. A contact on a gate is not
          chased, does not appear in the digest, and any running sequence ends.
          A track without one chases everybody on it for ever.
        </p>
      </div>

      {stages.length === 0 ? (
        <p className="text-sm text-destructive">
          This track has no gate. Nobody on it can ever be closed — they are
          chased for ever and nothing reports it. Add one.
        </p>
      ) : (
        <ul className="divide-y rounded-md border bg-background">
          {stages.map((stage) => {
            const rows = byStage.get(stageKey(stage.stage, stage.track));
            const on = rows?.length ?? 0;
            const verdict = stageRemovalVerdict(stage, on, allStages);
            return (
              <li
                key={stage.id}
                className="flex flex-wrap items-center gap-3 p-3"
              >
                <span className="font-medium">{stage.stage}</span>
                <span className="text-xs text-muted-foreground">
                  {countsUnknown
                    ? "count unknown"
                    : on === 0
                      ? "nobody here"
                      : `${on} ${on === 1 ? "contact" : "contacts"} here`}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-7 text-destructive"
                  disabled={busy || !verdict.ok}
                  title={verdict.ok ? undefined : verdict.reason}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await deleteStage(dataProvider, stage);
                      await invalidateStageSettings(queryClient);
                      notify(`${stage.stage} removed.`, { type: "success" });
                    } catch (e) {
                      notify((e as Error).message ?? "That did not work", {
                        type: "error",
                      });
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Remove
                </Button>
                {!verdict.ok && (
                  <p className="w-full text-xs text-muted-foreground">
                    {verdict.reason}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AddStage track={track} allStages={allStages} terminal />
    </section>
  );
};
